import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { LocalDate } from "@js-joda/core";
import type { StaffProfileData } from "services/api/company/staff.service";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";
import type { EmploymentDetailsForm } from "../../profilePanel.contract";
import type {
  DiscardChangesTarget,
  ModalState,
  TranslateFn,
} from "../profilePanel.state.contract";
import { toEmploymentDetailsForm } from "../profilePanel.state.formMappers.logic";
import {
  DraftTaxItem,
  addTaxDraftItem,
  deleteTaxDraftItem,
  hasTaxDraftHistoryChanges,
  saveTaxDraftHistory,
  toTaxDraftHistory,
  updateTaxDraftValue,
} from "../profilePanel.state.tax.logic";
import { toDefaultSalaryChangeDate } from "../profilePanel.state.template.logic";

interface UseProfilePanelTaxModalStateParams {
  companyId: number | null;
  currentEmploymentStartDate: LocalDate | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  effectiveFromDate: LocalDate | null;
  executeSavingAction: (action: () => Promise<void>) => void;
  isOpen: boolean;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  setDiscardChangesTarget: Dispatch<SetStateAction<DiscardChangesTarget>>;
  setEditedEmploymentDetails: Dispatch<SetStateAction<EmploymentDetailsForm>>;
  setEffectiveFromDate: Dispatch<SetStateAction<LocalDate | null>>;
  setModalState: Dispatch<SetStateAction<ModalState>>;
  setSaveError: (value: string) => void;
  translate: TranslateFn;
  workerId: number | null;
}

interface UseProfilePanelTaxModalStateResult {
  readonly taxDraftHistory: DraftTaxItem[];
  readonly handleAddTaxModal: () => void;
  readonly handleCloseTaxModal: () => void;
  readonly handleConfirmTaxModal: () => void;
  readonly handleDeleteTaxModal: (taxId: number) => void;
  readonly handleOpenTaxModal: () => void;
  readonly handleUpdateTaxDraftValue: (taxId: number, value: number) => void;
}

export const useProfilePanelTaxModalState = (
  params: UseProfilePanelTaxModalStateParams,
): UseProfilePanelTaxModalStateResult => {
  const [taxDraftHistory, setTaxDraftHistory] = useState<DraftTaxItem[]>([]);
  const [taxInitialHistory, setTaxInitialHistory] = useState<DraftTaxItem[]>(
    [],
  );
  const tempDraftIdRef = useRef(-1);

  useEffect(() => {
    if (!params.isOpen) {
      setTaxDraftHistory([]);
      setTaxInitialHistory([]);
    }
  }, [params.isOpen, params.workerId]);

  const handleOpenTaxModal = useCallback((): void => {
    const nextHistory = toTaxDraftHistory(params.profile);
    params.setEditedEmploymentDetails(toEmploymentDetailsForm(params.profile));
    setTaxDraftHistory(nextHistory);
    setTaxInitialHistory(nextHistory);
    params.setEffectiveFromDate(
      toDefaultSalaryChangeDate(
        getStockholmLocalDateNow(),
        params.currentEmploymentStartDate,
      ),
    );
    params.setSaveError("");
    params.setModalState({ type: "editTax" });
  }, [params]);

  const handleCloseTaxModal = useCallback((): void => {
    if (params.isSaving) return;

    const isDirty = hasTaxDraftHistoryChanges({
      taxDraftHistory,
      taxInitialHistory,
    });

    if (isDirty) {
      params.setDiscardChangesTarget("tax");
      return;
    }

    params.setSaveError("");
    params.setModalState({ type: "none" });
  }, [params, taxDraftHistory, taxInitialHistory]);

  const handleConfirmTaxModal = useCallback((): void => {
    saveTaxDraftHistory({
      companyId: params.companyId,
      executeSavingAction: params.executeSavingAction,
      isSaving: params.isSaving,
      onWorkerUpdated: params.onWorkerUpdated,
      profile: params.profile,
      reloadProfile: params.reloadProfile,
      taxDraftHistory,
      taxInitialHistory,
      closeModal: (): void => params.setModalState({ type: "none" }),
      setSaveError: params.setSaveError,
      translate: params.translate,
      workerId: params.workerId,
    });
  }, [params, taxDraftHistory, taxInitialHistory]);

  const handleAddTaxModal = useCallback((): void => {
    const nextItem = addTaxDraftItem({
      currentEmploymentStartDate: params.currentEmploymentStartDate,
      editedEmploymentDetails: params.editedEmploymentDetails,
      effectiveFromDate: params.effectiveFromDate,
      nextDraftId: tempDraftIdRef.current--,
      setSaveError: params.setSaveError,
      translate: params.translate,
    });
    if (nextItem == null) return;

    setTaxDraftHistory((previousValue) => [...previousValue, nextItem]);
    params.setSaveError("");
  }, [params]);

  const handleDeleteTaxModal = useCallback(
    (taxId: number): void => {
      setTaxDraftHistory((previousValue) =>
        deleteTaxDraftItem({
          taxDraftHistory: previousValue,
          taxId,
        }),
      );
      params.setSaveError("");
    },
    [params],
  );

  const handleUpdateTaxDraftValue = useCallback(
    (taxId: number, value: number): void => {
      setTaxDraftHistory((previousValue) =>
        updateTaxDraftValue({
          taxDraftHistory: previousValue,
          taxId,
          value,
        }),
      );
      params.setSaveError("");
    },
    [params],
  );

  return {
    taxDraftHistory,
    handleAddTaxModal,
    handleCloseTaxModal,
    handleConfirmTaxModal,
    handleDeleteTaxModal,
    handleOpenTaxModal,
    handleUpdateTaxDraftValue,
  };
};
